import { useEffect, useMemo, useState } from "react";
import {
  AlertOctagon,
  ArrowRight,
  CheckCircle2,
  Clock3,
  RadioTower,
  ShieldAlert,
  Siren,
  X,
} from "lucide-react";
import { listAlertSla } from "../api";
import type { AlertSla, Vehicle } from "../types";

const statusLabel = {
  breached: "SLA VENCIDO",
  due_soon: "POR VENCER",
  on_track: "EN TIEMPO",
};

function countdown(deadlineAt: string, now: number) {
  const seconds = Math.round((new Date(deadlineAt).getTime() - now) / 1000);
  const absolute = Math.abs(seconds);
  const hours = Math.floor(absolute / 3600);
  const minutes = Math.floor((absolute % 3600) / 60);
  const remainder = absolute % 60;
  const rendered = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
  return seconds < 0 ? `+${rendered}` : `-${rendered}`;
}

export function AlertSlaPanel({
  open,
  onClose,
  vehicles,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<AlertSla[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      listAlertSla()
        .then((items) => {
          setRows(items);
          setSelectedId((current) =>
            items.some((item) => item.alert_id === current)
              ? current
              : items[0]?.alert_id ?? null,
          );
          setError("");
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : "No fue posible cargar los incidentes"),
        );
    };
    refresh();
    const refreshTimer = window.setInterval(refresh, 30_000);
    const clockTimer = window.setInterval(() => setNow(Date.now()), 1_000);
    window.addEventListener("gsf:sla-updated", refresh);
    return () => {
      window.clearInterval(refreshTimer);
      window.clearInterval(clockTimer);
      window.removeEventListener("gsf:sla-updated", refresh);
    };
  }, [open]);

  const selected = rows.find((row) => row.alert_id === selectedId) || rows[0];
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selected?.vehicle_id);
  const summary = useMemo(
    () => ({
      breached: rows.filter((row) => row.sla_status === "breached").length,
      dueSoon: rows.filter((row) => row.sla_status === "due_soon").length,
      onTrack: rows.filter((row) => row.sla_status === "on_track").length,
    }),
    [rows],
  );

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel sla-panel" role="dialog" aria-modal="true" aria-labelledby="sla-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">CONTROL DE RESPUESTA · ESCALAMIENTO AUTOMÁTICO</span>
            <h2 id="sla-title">SLA de incidentes C5</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar SLA"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><AlertOctagon size={17} /><b>{summary.breached}</b> vencidos</span>
          <span><Clock3 size={17} /><b>{summary.dueSoon}</b> por vencer</span>
          <span><CheckCircle2 size={17} /><b>{summary.onTrack}</b> en tiempo</span>
        </section>
        <div className="sla-panel__body">
          <section className="sla-queue">
            <div className="admin-section-title">
              <span className="eyebrow">COLA NACIONAL ACTIVA</span>
              <b>{rows.length} incidentes</b>
            </div>
            {rows.map((row) => {
              const vehicle = vehicles.find((item) => item.id === row.vehicle_id);
              return (
                <button
                  type="button"
                  className={`sla-item sla-item--${row.sla_status}${selected?.alert_id === row.alert_id ? " is-selected" : ""}`}
                  onClick={() => setSelectedId(row.alert_id)}
                  key={row.alert_id}
                >
                  <span><i className={`status-dot is-${row.severity}`} /></span>
                  <span>
                    <small>{vehicle?.unit_code || "SISTEMA"} · {statusLabel[row.sla_status]}</small>
                    <strong>{row.title}</strong>
                    <em>{row.escalation_owner}</em>
                  </span>
                  <b>{countdown(row.deadline_at, now)}</b>
                </button>
              );
            })}
            {!rows.length && <p className="maintenance-empty">SIN INCIDENTES ACTIVOS</p>}
          </section>
          <section className="sla-detail">
            {selected && (
              <>
                <div className={`sla-clock sla-clock--${selected.sla_status}`}>
                  <Clock3 size={25} />
                  <span><small>{statusLabel[selected.sla_status]}</small><b>{countdown(selected.deadline_at, now)}</b></span>
                </div>
                <div className="sla-detail__title">
                  <span className="eyebrow">{selectedVehicle?.unit_code || "SISTEMA"} · NIVEL {selected.escalation_level}</span>
                  <h3>{selected.title}</h3>
                  <p>Objetivo {selected.target_minutes} min · {selected.severity.toUpperCase()}</p>
                </div>
                <div className="sla-owner">
                  <RadioTower size={18} />
                  <span><small>RESPONSABLE ACTUAL</small><strong>{selected.escalation_owner}</strong></span>
                </div>
                <div className="sla-protocol">
                  <span className="eyebrow"><ShieldAlert size={13} /> {selected.protocol_name}</span>
                  <ol>
                    {selected.protocol_steps.map((step, index) => (
                      <li key={step}><b>{index + 1}</b><span>{step}</span></li>
                    ))}
                  </ol>
                </div>
                <div className="sla-escalation">
                  <Siren size={18} />
                  <span>
                    <small>RUTA DE ESCALAMIENTO</small>
                    <strong>Operador → Supervisor → Seguridad → Dirección</strong>
                  </span>
                </div>
                {selectedVehicle && (
                  <button type="button" className="primary-button" onClick={() => { onSelectVehicle(selectedVehicle); onClose(); }}>
                    ABRIR UNIDAD <ArrowRight size={14} />
                  </button>
                )}
              </>
            )}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
